import { useGetFilesOwnerFileIdPresign } from "openapi/api/endpoints"

import { asArray } from "@/lib/as-array"
import type { FileMetadata } from "openapi/api/model"

/**
 * How many files are attached to a candidate.
 *
 * The record's count was hard-coded `undefined` behind a note saying "there is
 * no endpoint that lists a candidate's files — the contract has upload, delete,
 * rename and presign-by-id and nothing else". That reading was wrong, and the
 * generated name is why: `getFilesOwnerFileIdPresign(owner, fileId)` is
 * `GET /files/{owner}/{id}/presign`, where `owner` is `'job' | 'candidate'` and
 * the argument orval calls `fileId` is the *owner's* id. It answers with a list
 * — `{ urls: FileMetadata[] }` — which is exactly what the job's Files tab has
 * been reading all along.
 *
 * Counted rather than listed here, because a count is all the record's bar
 * shows. `isCounted` is false while the request is out and after it fails, so
 * the bar can leave the figure out rather than print a zero: a candidate with
 * attachments reading "Files 0" is worse than one reading nothing.
 */
export function useCandidateFiles(candidateId?: string) {
  const files = useGetFilesOwnerFileIdPresign("candidate", candidateId ?? "", {
    query: { enabled: Boolean(candidateId) },
  })

  const isCounted = Boolean(candidateId) && !files.isPending && !files.isError

  return {
    count: asArray<FileMetadata>(files.data?.urls).length,
    isCounted,
  }
}
